import React, { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { ActiveCallCard } from '../../components/supervisor/ActiveCallCard';
import { SupervisorControls } from '../../components/supervisor/SupervisorControls';
import {
  useActiveCallsForMonitoring,
  useActiveCallsRealtime,
  useSupervisorSessionsRealtime,
  useStartMonitoring,
  useChangeSessionType,
  useEndSession,
} from '../../lib/hooks/useSupervisorMonitoring';
import type { SupervisionType } from '../../lib/types/supervisor-monitoring';

export default function SupervisorMonitoring() {
  const [companyId] = useState<string>(() => {
    // Get company_id from user session/context
    // This is a placeholder - should come from auth context
    return localStorage.getItem('company_id') || '';
  });

  const [supervisorId] = useState<string>(() => {
    // Get supervisor_id from user session/context
    return localStorage.getItem('user_id') || '';
  });

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [currentSupervisionType, setCurrentSupervisionType] =
    useState<SupervisionType>('monitor');
  const [isSupervisorMuted, setIsSupervisorMuted] = useState(false);

  // Queries
  const {
    data: activeCalls = [],
    isLoading,
    error,
    refetch,
  } = useActiveCallsForMonitoring(companyId);

  // Realtime subscriptions
  useActiveCallsRealtime(companyId);
  useSupervisorSessionsRealtime(companyId);

  // Mutations
  const startMonitoringMutation = useStartMonitoring();
  const changeSessionTypeMutation = useChangeSessionType();
  const endSessionMutation = useEndSession();

  // Handlers
  const handleStartMonitoring = async (callId: string, conferenceName: string) => {
    if (!conferenceName) {
      alert('No conference name available for this call');
      return;
    }

    try {
      const session = await startMonitoringMutation.mutateAsync({
        companyId,
        supervisorId,
        callId,
        conferenceName,
        sessionType: 'monitor',
      });

      setActiveSessionId(session.id);
      setCurrentSupervisionType('monitor');

      // Here we would also initiate the Twilio connection
      // This will be implemented in the Twilio integration phase
      console.log('Starting monitoring session:', session);
    } catch (err) {
      console.error('Failed to start monitoring:', err);
      alert('Failed to start monitoring session');
    }
  };

  const handleChangeSupervisionType = async (newType: SupervisionType) => {
    if (!activeSessionId) return;

    try {
      await changeSessionTypeMutation.mutateAsync({
        sessionId: activeSessionId,
        fromType: currentSupervisionType,
        toType: newType,
      });

      setCurrentSupervisionType(newType);

      // Here we would update the Twilio connection parameters
      // This will be implemented in the Twilio integration phase
      console.log('Changed supervision type to:', newType);
    } catch (err) {
      console.error('Failed to change supervision type:', err);
      alert('Failed to change supervision mode');
    }
  };

  const handleEndSession = async () => {
    if (!activeSessionId) return;

    try {
      await endSessionMutation.mutateAsync(activeSessionId);
      setActiveSessionId(null);
      setCurrentSupervisionType('monitor');

      // Here we would disconnect the Twilio connection
      // This will be implemented in the Twilio integration phase
      console.log('Ended monitoring session');
    } catch (err) {
      console.error('Failed to end session:', err);
      alert('Failed to end monitoring session');
    }
  };

  const handleToggleMute = () => {
    setIsSupervisorMuted(!isSupervisorMuted);
    // Here we would mute/unmute the Twilio connection
    // This will be implemented in the Twilio integration phase
    console.log('Supervisor muted:', !isSupervisorMuted);
  };

  // Find the currently monitored call
  const monitoredCall = activeCalls.find((call) =>
    activeSessionId ? call.is_being_monitored : false
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Supervisor Monitoring
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Monitor and coach agents in real-time
              </p>
            </div>
            <button
              onClick={() => refetch()}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Error loading calls</h3>
              <p className="text-sm text-red-700 mt-1">
                {error instanceof Error ? error.message : 'Unknown error occurred'}
              </p>
            </div>
          </div>
        )}

        {/* Active Session Controls */}
        {activeSessionId && (
          <div className="mb-6">
            <SupervisorControls
              currentType={currentSupervisionType}
              onChangeType={handleChangeSupervisionType}
              onEndSession={handleEndSession}
              isMuted={isSupervisorMuted}
              onToggleMute={handleToggleMute}
              disabled={false}
            />
          </div>
        )}

        {/* Active Calls Grid */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Active Calls ({activeCalls.length})
            </h2>
            {isLoading && (
              <span className="text-sm text-gray-600">Loading calls...</span>
            )}
          </div>

          {activeCalls.length === 0 && !isLoading ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Active Calls
              </h3>
              <p className="text-gray-600">
                There are currently no active calls to monitor.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeCalls.map((call) => (
                <ActiveCallCard
                  key={call.call_id}
                  call={call}
                  onMonitor={handleStartMonitoring}
                  isMonitoring={call.call_id === monitoredCall?.call_id}
                  currentSupervisionType={
                    call.call_id === monitoredCall?.call_id
                      ? currentSupervisionType
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">
            How to Use Supervisor Monitoring
          </h3>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• Click "Start Monitoring" on any active call to begin</li>
            <li>• Use the control panel to switch between monitoring modes</li>
            <li>• Monitor mode: Listen silently without being heard</li>
            <li>• Whisper mode: Coach the agent privately</li>
            <li>• Barge mode: Join the conversation with all parties</li>
            <li>• Takeover mode: Take control of the call</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
